import { CancelToken } from 'axios';
import client from '@/utils/request';
import { getApiUrlWithParams } from '@/utils/url';
import {
  LOG_API_BASE_URL,
  TANZHI_API_BASE_URL as API_BASE_URL,
} from './base_url';
import { PRODUCTION_URL } from '@/constants/router-path';
import { ResumeBuilderType } from '@/types/resume';

/**
 * HomePage apis
 */

export const getRecommendJobTitles = () => {
  return client.get(`${API_BASE_URL}/job_titles/recommended_job_titles/`);
};

export const searchByQuery = (
  query: string,
  type = 'name_search',
  need_alias = true,
) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/search_by_query/`, {
    query,
    type,
    need_alias,
  });
  return client.get(requestUrl);
};

/**
 * Job titles apis
 */

export const getJobTitleData = (id: string, city?: string) => {
  const url = getApiUrlWithParams(`${API_BASE_URL}/job_titles/${id}/`, {
    city,
  });
  return client.get(url);
};

export const getCityComparison = (
  jobTitleId: string,
  city: string,
  comparedCity: string,
) => {
  return client.get(
    `${API_BASE_URL}/job_titles/${jobTitleId}/city_comparison/`,
    {
      cities: [city, comparedCity],
    },
  );
};

export const getJobTitleVote = (jobTitleId: string) => {
  return client.get(`${API_BASE_URL}/vote/${jobTitleId}/`);
};

export const updateJobTitleVote = (
  jobTitleId: string,
  vote: Record<string, number>,
) => {
  return client.post(`${API_BASE_URL}/vote/${jobTitleId}/`, { rating: vote });
};

export const getJobTitleCategoryOptions = () => {
  return client.get(`${API_BASE_URL}/job_titles/job_title_category_options/`);
};

export const getDescendantsOfCategory = (id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/descendants_of_category/`,
    {
      job_title_id: id,
    },
  );
  return client.get(requestUrl);
};

export const getVotePermission = (jobTitleId: string) => {
  return client.get(`${API_BASE_URL}/job_titles/${jobTitleId}/vote/`);
};

export const getJobTitleCity = (jobTitleId: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/province_city_data_by_id`,
    {
      job_title_id: jobTitleId,
    },
  );
  return client.get(requestUrl);
};

export const getHotJobOpportunities = (query: {
  jobTitle: string;
  city?: string;
  size?: number;
  offset?: number;
}) => {
  const url = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/${query.jobTitle}/hot_job_opportunities/`,
    query,
  );
  return client.get(url);
};

/**
 * Subscribe apis
 */
export const subscribeJob = (
  email: string,
  job: { id: string; name: string },
  location: { id: string; name: string },
) => {
  return client.post(`${API_BASE_URL}/job_subscriptions/`, {
    email,
    job,
    location,
  });
};

export const cancelSubscribeJob = (cancelId: string) => {
  return client.get(
    `${API_BASE_URL}/job_subscriptions/${cancelId}/cancel_job_subscription/`,
  );
};

/**
 * Job ranking apis
 */

export const getJobTitleRankData = (
  id?: string,
  city?: string,
  page?: number,
) => {
  const url = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/job_title_rank/`,
    {
      job_title_id: id,
      city,
      page,
    },
  );
  return client.get(url);
};

/**
 * Job board apis
 */
export const submitResume = (job_id: string, resume_id: string) => {
  return client.post(`${API_BASE_URL}/job_opportunities/submit_resume/`, {
    job_id,
    resume_id,
  });
};

/**
 * JobSeeker profile apis
 */
export const getJobSeekerProfile = () => {
  return client.get(`${API_BASE_URL}/jobseeker_profiles/my_profile/`);
};

export const updateJobSeekerProfile = (data: any) => {
  return client.patch(`${API_BASE_URL}/jobseeker_profiles/my_profile/`, data);
};

export const getJobSeekerJobDetail = (id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_profiles/job_detail/`,
    {
      flow_id: id,
    },
  );
  return client.get(requestUrl);
};

export const jobseekerIntentionJob = (data: any) => {
  return client.patch(
    `${API_BASE_URL}/jobseeker_profiles/intention_job/`,
    data,
  );
};

export const getSkills = () => {
  return client.get(`${API_BASE_URL}/jobseeker_profiles/skills`);
};

export const getProjectExperiences = () => {
  return client.get(`${API_BASE_URL}/jobseeker_profiles/project_experiences`);
};

export const searchProjectExperiences = (category: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_profiles/search_project_experience`,
    {
      category,
    },
  );
  return client.get(requestUrl);
};

/**
 * JobSeeker resumes apis
 */
export const getJobSeekerResumes = () => {
  return client.get(`${API_BASE_URL}/jobseeker_resumes/get_resumes/`);
};

export const getJobSeekerResume = (id: string) => {
  return client.get(`${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`);
};

export const createJobSeekerResume = () => {
  return client.post(`${API_BASE_URL}/jobseeker_resumes/create_resume/`);
};

export const updateJobSeekerResume = (
  data: Partial<ResumeBuilderType>,
  id: string,
) => {
  return client.patch(
    `${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`,
    data,
  );
};

export const copyJobSeekerResume = (id: string) => {
  return client.post(`${API_BASE_URL}/jobseeker_resumes/${id}/copy_resume/`);
};

export const deleteJobSeekerResume = (id: string) => {
  return client.delete(`${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`);
};

export const updateJobSeekerResumeAvatar = (data: Blob, id: string) => {
  const formData = new FormData();
  formData.append('avatar', data);

  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/${id}/resume_avatar/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

// 对应后端接口已删除
export const uploadDesignWorkImage = (image: File, id: string) => {
  const formData = new FormData();
  formData.append('image', image);
  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/${id}/resume_design_work_image/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

// 对应后端接口已删除
export const deleteDesignWorkImage = (image: string, id: string) => {
  return client.delete(
    `${API_BASE_URL}/jobseeker_resumes/${id}/resume_design_work_image/`,
    {
      image,
    },
  );
};

// 对应后端接口已删除
export const getResumeDiagnosis = (id: string) => {
  return client.get(
    `${API_BASE_URL}/jobseeker_resumes/${id}/resume_diagnosis/`,
  );
};

// 对应后端接口已删除
export const ignoreResumeDiagnosis = (
  id: string,
  experience_type: string,
  diagnosis_type: string,
  diagnosis_key: string,
  index?: number,
) => {
  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/${id}/diagnosis_ignore/`,
    {
      experience_type,
      diagnosis_type,
      diagnosis_key,
      index: index || 0,
    },
  );
};

export const uploadJobSeekerResume = (file: File | null) => {
  if (!file) {
    return;
  }
  const formData = new FormData();
  formData.append('resume_file', file);
  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/upload_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const getUploadResumeStatus = (task_id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_resumes/upload_resume_status/`,
    {
      id: task_id,
    },
  );
  return client.get(requestUrl);
};

/**
 * JobSeeker evaluation resume apis
 */
export const uploadEvaluationResume = (
  expect_job_title: { id: string; name: string },
  file?: File | null,
) => {
  const formData = new FormData();
  file && formData.append('resume_file', file);
  expect_job_title &&
    formData.append('expect_job_title', JSON.stringify(expect_job_title));
  return client.post(
    `${API_BASE_URL}/evaluation_resume/upload_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const evaluateResume = (
  resume_id: string,
  expect_job_title: { id: string; name: string } | null,
) => {
  const formData = new FormData();
  expect_job_title &&
    formData.append('expect_job_title', JSON.stringify(expect_job_title));
  return client.post(
    `${API_BASE_URL}/evaluation_resume/${resume_id}/evaluate_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const getEvaluationProgress = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/get_evaluation_progress/`,
  );
};

export const saveEvaluationResume = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/save_evaluation_resume/`,
  );
};

export const getEvaluationResume = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/get_evaluation_resume/`,
  );
};

export const getEvaluationResumes = () => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/get_evaluation_resumes/`,
  );
};

export const deleteEvaluationResume = (id: string) => {
  return client.delete(
    `${API_BASE_URL}/evaluation_resume/${id}/delete_evaluation_resume/`,
  );
};

export const saveEvaluationResumeTitle = (
  data: { title: string },
  id: string,
) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/evaluation_resume/${id}/evaluation_title/`,
    { title: data.title },
  );
  return client.patch(requestUrl);
};

export const createEvaluationPaymentOrder = (id: string) => {
  return client.get(`${API_BASE_URL}/evaluation_resume/${id}/payment_order/`);
};

export const downloadEvaluationReport = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/download_report/`,
    {},
    { responseType: 'blob' },
  );
};

export const getPreviewResume = (
  resume_id: string,
  template_id?: string,
  cancelToken?: CancelToken,
) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_resumes/${resume_id}/preview_resume/`,
    { template_id },
  );
  return client.get(requestUrl, {}, { cancelToken, responseType: 'blob' });
};

export const getResumeTemplates = () => {
  return client.get(`${API_BASE_URL}/resume_templates/get_templates/`);
};

/**
 * JobSeeker rewritten resume apis
 */
export const createRewrittenPaymentOrder = (id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/rewritten_resume/payment_order/`,
    { id },
  );
  return client.get(requestUrl);
};

export const getRewrittenStatus = (resume_id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/rewritten_resume/get_rewritten_status/`,
    {
      id: resume_id,
    },
  );
  return client.get(requestUrl);
};

/**
 * Employer profile apis
 */
export const getEmployerProfile = () => {
  return client.get(`${API_BASE_URL}/employer_profiles/my_profile/`);
};

export const updateEmployerProfile = (data: any) => {
  return client.patch(`${API_BASE_URL}/employer_profiles/my_profile/`, data);
};

export const initEmployerProfile = (data: any) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key] ? data[key] : '');
  });
  return client.post(
    `${API_BASE_URL}/employer_profiles/profile_initialization/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

export const getAllBenefits = () => {
  return client.get(`${API_BASE_URL}/benefits/`);
};

/**
 * Job apis
 */
export const getJobs = () => {
  return client.get(`${API_BASE_URL}/jobs/my_jobs/`);
};

export const getOutdatedJobs = () => {
  return client.get(`${API_BASE_URL}/jobs/my_outdated_jobs/`);
};

export const getCandidateReport = (id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobs/candidate_detail/`,
    {
      flow_id: id,
    },
  );
  return client.get(requestUrl);
};

export const getOpenJobs = (jobTitleId: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_profiles/suggested_jobs/`,
    {
      category: jobTitleId,
    },
  );
  return client.get(requestUrl);
};

export const createJob = (data: any) => {
  return client.post(`${API_BASE_URL}/jobs/`, data);
};

export const updateJob = (data: any, jobId: string) => {
  return client.patch(`${API_BASE_URL}/jobs/${jobId}/`, data);
};

export const getCandidates = (jobId: string) => {
  return client.get(`${API_BASE_URL}/jobs/${jobId}/candidates/`);
};

/**
 * Company apis
 */
export const getCompany = () => {
  return client.get(`${API_BASE_URL}/companies/my_company/`);
};

export const updateCompany = (id: string, data: any) => {
  return client.patch(`${API_BASE_URL}/companies/${id}/`, data);
};

export const getBenefits = () => {
  return client.get(`${API_BASE_URL}/benefits/`);
};

export const uploadCompanyImage = (image: File) => {
  const formData = new FormData();
  formData.append('image', image);
  return client.post(`${API_BASE_URL}/company_image/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadCompanyVideo = (video: File) => {
  const formData = new FormData();
  formData.append('video', video);
  return client.post(`${API_BASE_URL}/company_video/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteCompanyMedia = (url: string) => {
  return client.delete(`${API_BASE_URL}/company_file/`, { url });
};

export const getJobCompanyCity = (companyId: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/companies/jobs/province_city_data_by_id`,
    {
      id: companyId,
    },
  );
  return client.get(requestUrl);
};

export const searchCompanyJDJobTitleByQuery = (
  companyId: string,
  query: string | null,
) => {
  return client.get(
    `${API_BASE_URL}/companies/${companyId}/job_title_name_search/`,
    {
      query,
    },
  );
};

export const searchCompanyJob = (
  companyId: string,
  jobTitleId?: string,
  locationId?: string,
  // salaryRange?: { min: number, max: number},
) => {
  const params: Record<string, any> = {};
  if (jobTitleId) {
    params.job_title_id = jobTitleId;
  }
  if (locationId) {
    params.location_id = locationId;
  }
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/companies/${companyId}/search_job/`,
    params,
  );
  return client.get(requestUrl);
};

/**
 *
 * KGC concept model apis
 */
export const getCityOptions = (query: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/locations/search/`, {
    query,
  });
  return client.get(requestUrl);
};

export const getProvinceCityData = () => {
  return client.get(`${API_BASE_URL}/locations/province_city_data/`);
};

export const getSchoolOptions = (school: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/schools/search/`, {
    query: school,
  });
  return client.get(requestUrl);
};

export const getMajorOptions = (major: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/majors/search/`, {
    query: major,
  });
  return client.get(requestUrl);
};

export const getCompanyOptions = (query: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/companies/search/`, {
    query,
  });
  return client.get(requestUrl);
};

export const getIndustryData = () => {
  return client.get(`${API_BASE_URL}/industries/industry_data/`);
};

/**
 * Other apis
 *
 */
export const uploadAvatar = (data: Blob) => {
  const formData = new FormData();
  formData.append('avatar', data);
  return client.post(`${API_BASE_URL}/user_avatar/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getBusinessLicenseData = (data: Blob) => {
  const formData = new FormData();
  formData.append('image', data);
  return client.post(`${API_BASE_URL}/business_license/parse/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const resumeExport = (html: string, cancelToken?: CancelToken) => {
  return client.post(
    '/tools/resume-export/',
    { html },
    { responseType: 'blob', cancelToken },
  );
};

export const interviewContact = (data: any) =>
  client.post(`${API_BASE_URL}/interview_requests/`, data);

/**
 * wordPress apis
 */

export const getArticleMedia = (id: number) => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/media/${id}?_fields=source_url`,
  );
};

export const getArticles = () => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/posts?per_page=100&_fields=title,id,date,excerpt,link,categories,featured_media`,
  );
};

export const getArticleTags = () => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/tags?per_page=100&_fields=id,name`,
  );
};

export const getArticlesByTags = (tagIds: string) => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/posts?tags=${tagIds}&per_page=100&_fields=title,id,date,excerpt,link,tags,featured_media`,
  );
};

/**
 * event tracker apis
 */

export const sendEventTracker = (
  data: {
    name: string;
    [key: string]:
      | string
      | number
      | Record<string, string | number | null>[]
      | null
      | undefined;
  },
  options?: Record<string, unknown>,
) => {
  return client.post(`${LOG_API_BASE_URL}/`, data, options);
};

/**
 * error apis
 */
export const sendError = (data: any) => {
  return client.post(`${API_BASE_URL}/error/error_report/`, data);
};
